import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Header, Footer } from './page.jsx';
import { useState } from 'react';
import savarImage from './images/savar.png';
import divaImage from './images/diva.jpg';
import hanaImage from './images/hana.jpg';
import krishImage from './images/krish.jpg';

const nikImage = 'https://i.imgur.com/BjjzPXF.jpeg';
const elijahImage = 'https://i.imgur.com/sNX90Dy.jpeg';


// Split data into Leadership Team
const chiefSuiteData = [
  {
    id: 1,
    name: "Nikhilesh Suravarjjala",
    role: "President & CEO",
    image: nikImage,
    description: "Hey, I'm Nikhilesh Suravarjjala. Tri-Valley Tech is the space where I bring ideas to life; from websites and apps to full community projects. I've always enjoyed building things that blend creativity with real impact, and TVT lets me do that while leading a team that's learning and growing alongside me. What matters to me most is creating tools and experiences that genuinely help people; whether that's a clean user interface, a simple app that solves a real problem, or an event that brings young people together. TVT is where I get to explore design, development, and leadership all at once, and I'm proud of the direction we're heading.",
    badges: ["Leadership", "Design", "Development"]
  },
  {
    id: 2,
    name: "Diva Rawal",
    role: "COO",
    image: divaImage,
    imagePosition: "center 62%",
    description: "Hi, I'm Diva Rawal and at Tri-Valley Tech I lead day-to-day operations as COO. I focus on planning, coordination, and long-term sustainability, making sure our programs have the structure and resources they need to deliver real outcomes. I work closely with our chapter leads and interns to keep our work aligned with our mission, and I care deeply about helping students grow through structured, well-supported opportunities.",
    badges: ["Operations", "Strategy", "Leadership"]
  },
  {
    id: 3,
    name: "Elijah Guan",
    role: "Secretary & CLO",
    image: elijahImage,
    imagePosition: "center 38%",
    description: "I'm Elijah Guan. As Secretary & CLO at Tri-Valley Tech, I oversee organizational records and legal operations while supporting the integrity of our programs and partnerships. I work on documentation, compliance, and policy clarity so our teams can build with confidence. My goal is to keep our structure strong, transparent, and aligned with our mission as we continue expanding opportunities for students.",
    badges: ["Legal Operations", "Governance", "Organization"]
  }
];

const chapterLeadsData = [
  {
    id: 4,
    name: "Savar Shandilya",
    role: "Fremont Chapter President",
    image: savarImage,
    description: "I am the Fremont Chapter President of TVT Cyber-Ed, where I work to bring accessible, hands-on cybersecurity and STEM education to students across the Tri-Valley area. As a high school student with a strong interest in technology, cybersecurity, and community impact, I am passionate about creating opportunities for students to develop practical digital safety skills that are increasingly essential in today's world. In my role, I focus on building engaging workshops, organizing chapter-led events, and helping launch initiatives that introduce cybersecurity concepts to students of all backgrounds. I am especially excited about TVT Cyber-Ed's mission to combine education with real-world tools and applications, empowering students to become more informed, confident, and responsible digital citizens. I am excited to help grow the Fremont chapter from the ground up and contribute to TVT Cyber-Ed's broader vision of making cybersecurity education accessible, impactful, and community-driven.",
    badges: ["Leadership", "Strategy", "Organization"]
  },
  {
    id: 5,
    name: "Hana Skulan",
    role: "San Ramon Chapter President",
    image: hanaImage,
    imagePosition: "center 30%",
    description: "Hi, I'm Hana, and I'm the San Ramon Chapter President of Tri-Valley Tech. I'm passionate about STEM, leadership, and giving students opportunities to explore technology and develop new skills. In my role, I help organize events, connect with students in our community, and lead initiatives that make technology more accessible and engaging. I'm proud to be part of Tri-Valley Tech and to help grow our San Ramon chapter!",
    badges: ["Leadership", "STEM", "Community"]
  },
  {
    id: 6,
    name: "Krish Parikh",
    role: "Oakland Chapter President",
    image: krishImage,
    imagePosition: "center 30%",
    description: "Hi, I'm Krish, and I'm the Oakland Chapter President of Tri-Valley Tech. I'm passionate about STEM, leadership, and policy and law, and I'm especially interested in how technology shapes the communities around us. I wanted to bring Tri-Valley Tech to Oakland because of the wealth gap in access to technology and STEM opportunities, and I believe every student deserves the chance to explore tech and develop new skills. In my role, I help organize events, connect with students in our community, and lead initiatives that make technology more accessible and engaging. Outside of TVT, I love spending time outdoors. I'm proud to be part of Tri-Valley Tech and to help grow our Oakland chapter!",
    badges: ["Leadership", "Policy & Law", "Community"]
  }
];

const OfficerCard = ({ officer, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <motion.div
      className="bg-[var(--card-bg)] rounded-xl overflow-hidden shadow-xl transform transition duration-300 hover:scale-105 hover:shadow-2xl flex flex-col border border-[var(--card-border)] backdrop-blur-sm hover:bg-[var(--hover-bg)]"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -5 }}
    >
      <div className="relative">
        <img 
          src={officer.image} 
          alt={officer.name} 
          className="w-full h-[16rem] md:h-[20rem] lg:h-[24rem] object-cover transition duration-300 transform hover:scale-110"
          style={{ objectPosition: officer.imagePosition || 'center' }}
        />
        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
          <p className="text-white text-lg font-semibold px-4 text-center">{officer.role}</p>
        </div>
      </div>
      <div className="p-6 flex-grow flex flex-col">
        <h3 className="text-2xl font-bold text-[var(--accent-primary)] mb-2">{officer.name}</h3>
        {officer.badges && (
          <div className="flex flex-wrap gap-2 mb-3">
            {officer.badges.map((badge, i) => (
              <span key={i} className="px-2 py-1 text-xs rounded-full bg-[var(--badge-bg)] text-[var(--badge-text)]">
                {badge}
              </span>
            ))}
          </div>
        )}
        <AnimatePresence>
          {isExpanded ? (
            <motion.div
              key="expanded"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="text-[var(--text-secondary)] text-sm flex-grow overflow-hidden"
            >
              {officer.description}
            </motion.div>
          ) : (
            <motion.div
              key="collapsed"
              initial={{ opacity: 1, height: 'auto' }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="text-[var(--text-secondary)] text-sm line-clamp-3 flex-grow overflow-hidden"
            >
              {officer.description}
            </motion.div>
          )}
        </AnimatePresence>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-4 text-[var(--accent-primary)] hover:text-[var(--accent-secondary)] transition-colors duration-300 self-start"
        >
          {isExpanded ? 'Read Less' : 'Read More'}
        </button>
      </div>
    </motion.div>
  );
};

const OfficersPage = () => {
  return (
    <div className="min-h-screen bg-[var(--bg-primary)] relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-[var(--accent-primary)] to-[var(--accent-secondary)] opacity-5"></div>
      <Header />
      <div className="container mx-auto px-4 py-20 mt-20 relative z-10">
        {/* Chief Suite Section */}
        <motion.h1
          className="text-5xl font-bold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)]"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Board of Directors
        </motion.h1>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12 items-start mb-24">
          {chiefSuiteData.map((officer, index) => (
            <OfficerCard key={officer.id} officer={officer} index={index} />
          ))}
        </div>

        {/* Chapter Leads Section */}
        <motion.h2
          className="text-5xl font-bold text-center mb-12 text-transparent bg-clip-text bg-gradient-to-r from-[var(--accent-primary)] to-[var(--accent-secondary)]"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Chapter Leads
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12 items-start">
          {chapterLeadsData.map((officer, index) => (
            <OfficerCard key={officer.id} officer={officer} index={index} />
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OfficersPage;
